import { createFileRoute } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/difference-between-flat-and-pitched-roof";
const TITLE = "Flat Roof vs Pitched Roof: Cost, Lifespan & Phoenix Pros and Cons";
const DESCRIPTION =
  "Side-by-side comparison of flat and pitched roofs: cost per square foot, lifespan, drainage, energy efficiency, and which one wins in the Phoenix climate. From a family-owned roofer since 1989.";

const faqs = [
  {
    q: "What is the main difference between a flat roof and a pitched roof?",
    a: "Pitch — the angle of the roof. A flat roof has a slope of less than 10° (typically 1°–3° for drainage); a pitched roof has a slope of 10° or steeper. The pitch dictates which materials work (foam and membrane on flat, tile and shingle on pitched), how water drains, and how the roof handles wind, sun, and snow.",
  },
  {
    q: "Which is cheaper, a flat roof or a pitched roof?",
    a: "Flat roofs are usually cheaper to install per square foot — $4–$10/sq ft for spray foam in Phoenix vs $7–$15/sq ft for tile or premium shingle. Flat roofs also use less material because the surface area equals the footprint, while a pitched roof's surface area is 15–40% larger than the home's footprint.",
  },
  {
    q: "Which lasts longer, flat or pitched?",
    a: "Pitched tile can last 50+ years; pitched shingle 20–30 years. A properly maintained spray foam flat roof lasts 25–40+ years with recoats every 5 years. The lifespan gap is smaller than people think — the deciding factor is maintenance, not pitch.",
  },
  {
    q: "Are flat roofs bad in Phoenix?",
    a: "No — flat roofs are extremely common and well-suited to Phoenix. Low rainfall (~8 in/yr) means drainage demands are modest, and spray foam delivers the highest R-value of any roofing system (R-6.5 to R-7 per inch), which cuts cooling bills 15–35%. The myth that flat roofs leak more applies to old built-up tar roofs, not modern foam systems.",
  },
  {
    q: "Can I convert a flat roof to a pitched roof or vice versa?",
    a: "Technically yes, but it's a structural job, not a roofing job — you're rebuilding the framing and trusses, then re-roofing. Cost runs $15,000–$40,000+ beyond the roof itself. Almost no Phoenix homeowner does this; we recommend matching the original roof type unless there's a specific reason to convert.",
  },
  {
    q: "Which roof type is more energy efficient?",
    a: "In Phoenix, a spray foam flat roof wins on energy. The foam itself is the insulation (R-10 to R-14 at typical thickness), and the reflective topcoat blocks 80–85% of solar radiation. A tile roof has good thermal mass but relies entirely on attic insulation underneath — there's no insulation in the tile itself.",
  },
];

export const Route = createFileRoute("/difference-between-flat-and-pitched-roof")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Flat vs Pitched Roof Guide",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="foam-roofing"
      eyebrow="Phoenix roofing guide"
      h1="Flat Roof vs Pitched Roof: Which Is Better?"
      subhead="Cost, lifespan, energy, and drainage compared — with a Phoenix-specific verdict from a roofer who's been installing both since 1989."
      heroBullets={[
        "Flat: $4–$10/sq ft · Pitched: $7–$15/sq ft",
        "Flat foam lasts 25–40+ yrs with recoats",
        "Pitched tile lasts 50+ yrs",
        "In Phoenix, foam flat roofs win on energy",
      ]}
      whatIs={{
        heading: "The real difference: pitch, materials, and how water gets off",
        body: (
          <>
            <p>
              The technical line is pitch — the angle of the roof from horizontal.
              A <strong>flat roof</strong> has a slope of less than 10° (usually 1°–3°, just
              enough to push water toward drains or scuppers). A <strong>pitched roof</strong> is
              10° or steeper, which lets gravity do the drainage work for you.
            </p>
            <p>
              That single number — pitch — drives almost every other difference. It dictates which
              materials you can use, how the roof handles rain and wind, what the labor costs
              look like, how long it lasts, and how it performs in extreme heat. Here's the
              side-by-side that actually matters for Phoenix homeowners:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-primary/5">
                    <th className="border border-foreground/15 p-2 text-left">Factor</th>
                    <th className="border border-foreground/15 p-2 text-left">Flat roof</th>
                    <th className="border border-foreground/15 p-2 text-left">Pitched roof</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border border-foreground/15 p-2"><strong>Typical pitch</strong></td><td className="border border-foreground/15 p-2">1°–10°</td><td className="border border-foreground/15 p-2">10°–45°+</td></tr>
                  <tr><td className="border border-foreground/15 p-2"><strong>Common materials</strong></td><td className="border border-foreground/15 p-2">Spray foam, TPO, EPDM, modified bitumen</td><td className="border border-foreground/15 p-2">Tile, asphalt shingle, metal</td></tr>
                  <tr><td className="border border-foreground/15 p-2"><strong>Install cost (Phoenix)</strong></td><td className="border border-foreground/15 p-2">$4–$10/sq ft</td><td className="border border-foreground/15 p-2">$7–$15/sq ft</td></tr>
                  <tr><td className="border border-foreground/15 p-2"><strong>Lifespan</strong></td><td className="border border-foreground/15 p-2">25–40+ yrs (foam, with recoats)</td><td className="border border-foreground/15 p-2">20–30 yrs (shingle), 50+ yrs (tile)</td></tr>
                  <tr><td className="border border-foreground/15 p-2"><strong>Maintenance</strong></td><td className="border border-foreground/15 p-2">Recoat every ~5 yrs in Phoenix</td><td className="border border-foreground/15 p-2">Spot tile/shingle repairs as needed</td></tr>
                  <tr><td className="border border-foreground/15 p-2"><strong>Energy in Phoenix</strong></td><td className="border border-foreground/15 p-2">Excellent — foam is insulation + reflective coating</td><td className="border border-foreground/15 p-2">Depends entirely on attic insulation</td></tr>
                  <tr><td className="border border-foreground/15 p-2"><strong>Drainage</strong></td><td className="border border-foreground/15 p-2">Engineered drains/scuppers</td><td className="border border-foreground/15 p-2">Gravity</td></tr>
                  <tr><td className="border border-foreground/15 p-2"><strong>Roof-mounted equipment</strong></td><td className="border border-foreground/15 p-2">Easy (HVAC, solar, decks)</td><td className="border border-foreground/15 p-2">Harder, more flashing</td></tr>
                  <tr><td className="border border-foreground/15 p-2"><strong>Curb appeal</strong></td><td className="border border-foreground/15 p-2">Modern / Southwestern</td><td className="border border-foreground/15 p-2">Traditional / Mediterranean</td></tr>
                </tbody>
              </table>
            </div>
          </>
        ),
      }}
      climate={{
        heading: "Phoenix verdict: which wins here?",
        body: (
          <>
            <p>
              Phoenix is one of the few markets where the conventional wisdom flips. Nationally,
              pitched roofs are considered the safer default — more drainage, longer shingle/tile
              life, easier repairs. In the Valley, several local factors change the math:
            </p>
            <ul>
              <li><strong>Low rainfall (~8 in/yr).</strong> The biggest historical knock on flat roofs — drainage — barely matters here. We don't get the sustained rain that punishes flat roofs in the Pacific Northwest or the Southeast.</li>
              <li><strong>Extreme solar load.</strong> Roof surface temps hit 160–180°F. A reflective foam roof rejects 80–85% of that heat before it ever reaches your attic. A tile roof relies entirely on whatever attic insulation you have — often R-38 and not much else.</li>
              <li><strong>R-6.5 to R-7 per inch.</strong> Spray foam has the highest R-value of any roofing system. A 1.5"–2" install gives you R-10 to R-14 from the roof itself, on top of any attic insulation. <a href="/foam-roof-energy-efficiency-r-value" className="text-primary underline">More on R-value and cooling-bill savings here.</a></li>
              <li><strong>Monsoon wind.</strong> A flat foam roof is one seamless monolithic membrane — there are no shingles to peel off in a 60 mph haboob. Tile holds up too; shingle is the weakest link in monsoon season.</li>
              <li><strong>Architectural fit.</strong> Most Phoenix homes built after 1980 — especially in Scottsdale, Cave Creek, Fountain Hills, North Phoenix — are designed around flat or low-slope roofs to match Southwestern and contemporary styles. Converting to pitched is a structural rebuild, not a roof job.</li>
            </ul>
            <p>
              <strong>Our take, roofing Phoenix since 1989:</strong> if your home was built flat, keep
              it flat and spray foam. If it was built pitched with tile, tile is hard to beat for
              another 50 years. Shingle is the option we'd argue with — it's the shortest-lived
              roof in this climate, and a tile or foam alternative usually pencils out better.
            </p>
          </>
        ),
      }}
      process={{
        heading: "How to decide for your home",
        intro:
          "If you're choosing between rebuilding your existing roof or switching types, run through these in order:",
        steps: [
          { title: "Match what's there", body: "Unless there's a structural reason to convert, match your existing roof type. Conversions are $15K–$40K+ on top of the roof itself and rarely pay back." },
          { title: "Check your attic insulation", body: "If you have a pitched tile roof and an underinsulated attic, fix the attic first — it's cheaper than re-roofing and has bigger energy impact." },
          { title: "Look at your roof equipment plans", body: "Adding solar, a second AC, or a roof deck? Flat is dramatically easier to work with — no flashing nightmares, no penetration leaks." },
          { title: "Factor in maintenance cadence", body: "Flat foam wants a recoat every 5 years in Phoenix. Tile is largely maintenance-free for decades. Shingle is somewhere in between but the shortest-lived." },
          { title: "Get a real number, not a range", body: "Pricing varies more by roof condition and complexity than by type. The only way to know is an on-site measurement." },
        ],
      }}
      whyChoose={{
        heading: "Common myths worth ignoring",
        body: (
          <>
            <ul>
              <li><strong>"Flat roofs always leak."</strong> Built-up tar-and-gravel roofs from the 70s and 80s leaked. Modern spray foam — properly installed and recoated on schedule — is the most leak-resistant residential roof system available. It's a single monolithic membrane with no seams to fail.</li>
              <li><strong>"Pitched roofs are always more energy efficient."</strong> Not in Phoenix. The pitch itself does nothing for energy; what matters is insulation and reflectivity. Foam beats tile on both counts.</li>
              <li><strong>"Flat roofs don't add home value."</strong> In Phoenix, flat-roof homes are the architectural norm in many neighborhoods. Buyers there expect them; pitched roofs would look out of place.</li>
              <li><strong>"Tile is too heavy for my house."</strong> Tile is heavy (~600–1,100 lbs per square), but homes built for tile are framed for it. The conversion math only matters if you're switching from shingle.</li>
            </ul>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Not sure which roof you have — or which you should have?"
      ctaBody="A free on-site inspection gets you a real assessment of your current roof, what it'd cost to repair vs replace, and an honest opinion on whether you should match what's there or change types. No pressure."
    />
  );
}
